"use client";

import type * as React from "react";
import { Box } from "../../components/ui/box";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export type PageDotsProps = Omittable<{
  /** How many pages the dots stand for. Renders nothing below 2. */
  count: number;
  /** Zero-based index of the current page. Clamped into range. */
  index: number;
  /**
   * Makes each dot a button that jumps to its page. Without it the dots are
   * a read-only position indicator (one `img` role naming the position).
   */
  onIndexChange?: (index: number) => void;
  /** Accessible name of the group. Default "Pages". */
  label?: string;
  ref?: React.Ref<HTMLDivElement>;
}>;

// The mark: a 6px dot at rest, a 16px bar for the current page. Width and
// ink transition together so a page change reads as the bar sliding.
function markClass(current: boolean, pressable: boolean): string {
  return cn(
    "block h-1.5 rounded-full transition-[width,background-color] duration-base",
    current
      ? "w-4 bg-foreground"
      : cn("w-1.5 bg-foreground/25", pressable && "group-hover:bg-foreground/50"),
  );
}

/**
 * A row of page dots — the position indicator of a stepped surface (a
 * pager, a carousel, an onboarding sequence). The current page is a 16px
 * bar; every other page is a 6px dot on a quarter-strength ink, so the row
 * reads as "where am I" before it reads as a control. Interactive when
 * `onIndexChange` is given, otherwise a single labelled image.
 */
export function PageDots({
  count,
  index,
  onIndexChange,
  label = "Pages",
  ref,
  ...rest
}: PageDotsProps & Omit<React.ComponentProps<"div">, "children">): React.ReactElement | null {
  if (count < 2) return null;
  const current = Math.min(Math.max(index, 0), count - 1);
  const pages = Array.from({ length: count }, (_, i) => i);

  if (!onIndexChange) {
    return (
      <Box
        align="center"
        aria-label={`Page ${current + 1} of ${count}`}
        data-slot="page-dots"
        direction="row"
        gap="xs"
        ref={ref}
        role="img"
        {...rest}
      >
        {pages.map((page) => (
          <Box
            key={page}
            className={markClass(page === current, false)}
            data-current={page === current || undefined}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box
      align="center"
      aria-label={label}
      data-slot="page-dots"
      direction="row"
      gap="xs"
      ref={ref}
      role="group"
      {...rest}
    >
      {pages.map((page) => (
        <Box
          key={page}
          aria-current={page === current ? "true" : undefined}
          aria-label={`Page ${page + 1} of ${count}`}
          as="button"
          // The 6px dot is the mark; the 18px padded box is the hit target,
          // pulled back by its own margin so the row keeps the 6px rhythm.
          className="group -m-1.5 cursor-pointer rounded-full p-1.5 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
          data-current={page === current || undefined}
          onClick={() => onIndexChange(page)}
          type="button"
        >
          <Box className={markClass(page === current, true)} />
        </Box>
      ))}
    </Box>
  );
}
